// context/CompanyContext.tsx
import React, {
	createContext,
	useContext,
	useState,
	useEffect,
	ReactNode,
	useRef,
} from 'react';
import { Modal } from 'antd';
import { CheckCircleOutlined } from '@ant-design/icons';
import styles from './modal.module.scss';

interface CompanyContextType {
	companyId: string | null;
	switchCompany: (newCompanyId: string) => void;
}

const CompanyContext = createContext<CompanyContextType | undefined>(undefined);

interface CompanyProviderProps {
	children: ReactNode;
}

export const CompanyProvider: React.FC<CompanyProviderProps> = ({
	children,
}) => {
	const [companyId, setCompanyId] = useState<string | null>(() =>
		localStorage.getItem('companyId')
	);
	const [modalVisible, setModalVisible] = useState(false);
	const modalMessage = useRef(
		'Your session was refreshed due to a company change.'
	);

	const switchCompany = (newCompanyId: string) => {
		localStorage.setItem('companyId', newCompanyId);
		localStorage.setItem(
			'companySwitched',
			JSON.stringify({ companyId: newCompanyId, timestamp: Date.now() })
		);
		setCompanyId(newCompanyId);
	};

	useEffect(() => {
		const handleStorage = (event: StorageEvent) => {
			if (event.key === 'companySwitched' && event.newValue) {
				try {
					const { companyId: newCompanyId } = JSON.parse(
						event.newValue
					);
					setCompanyId(newCompanyId);

					sessionStorage.setItem(
						'companyChangedReason',
						'refresh_due_to_company_switch'
					);
					window.location.reload();
				} catch (e) {
					console.error('Invalid companySwitched payload', e);
				}
			}
		};

		window.addEventListener('storage', handleStorage);
		return () => window.removeEventListener('storage', handleStorage);
	}, []);

	useEffect(() => {
		const reason = sessionStorage.getItem('companyChangedReason');
		if (reason === 'refresh_due_to_company_switch') {
			const delayShow = setTimeout(() => setModalVisible(true), 1000);
			const autoClose = setTimeout(() => setModalVisible(false), 4000);

			sessionStorage.removeItem('companyChangedReason');

			return () => {
				clearTimeout(delayShow);
				clearTimeout(autoClose);
			};
		}
	}, []);

	const closeModal = () => {
		setModalVisible(false);
	};

	return (
		<CompanyContext.Provider value={{ companyId, switchCompany }}>
			{children}

			<div className={styles.companyModalWrapper}>
				<Modal
					title={null}
					open={modalVisible}
					onCancel={closeModal}
					footer={null}
					closable={false}
					centered={false}
					maskClosable={true}
					className={styles.customModal}
					transitionName=""
					maskTransitionName=""
					width={400}
					style={{
						position: 'fixed',
						right: 24,
						top: 24,
						margin: 0,
					}}
				>
					<div className={styles.modalContent}>
						<div className={styles.modalHeader}>
							<div className={styles.iconWrapper}>
								<CheckCircleOutlined
									className={styles.successIcon}
								/>
							</div>
							<div className={styles.titleWrapper}>
								<h3 className={styles.modalTitle}>
									Session Refreshed
								</h3>
								<span className={styles.modalSubtitle}>
									Company switch successful
								</span>
							</div>
						</div>

						<div className={styles.modalBody}>
							<p className={styles.modalMessage}>
								{modalMessage.current}
							</p>
						</div>

						<div className={styles.modalFooter}>
							<div className={styles.progressBar}>
								<div className={styles.progressFill}></div>
							</div>
							<span className={styles.autoCloseText}>
								Auto-closing in 3 seconds
							</span>
						</div>
					</div>
				</Modal>
			</div>
		</CompanyContext.Provider>
	);
};

export const useCompany = (): CompanyContextType => {
	const context = useContext(CompanyContext);
	if (!context) {
		throw new Error('useCompany must be used within a CompanyProvider');
	}
	return context;
};
